import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import ChevronDownIconOld from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconOld from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';

export type Props = {
	isActive: boolean;
	isExpanded: boolean;
	isVisible?: boolean;
	label: string;
	id: string;
	size?: 'small' | 'medium' | 'large' | 'xlarge';
	removeCaret?: boolean;
	tabIndex?: number;
	onClick?: () => void;
};

export const ExpandButton = ({
	id,
	label,
	isActive,
	isExpanded,
	onClick = () => null,
	size = undefined,
	isVisible = true,
	removeCaret = false,
	// @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'number'.
	tabIndex = null,
}: Props) => {
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	const ChevronOld = isExpanded ? ChevronDownIconOld : ChevronRightIconOld;
	const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;
	const onKeydown = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
		(event) => {
			// Space bar or enter (Button A11y rules) triggers click of expand button
			if ([13, 32].includes(event.keyCode)) {
				event.preventDefault();
				onClick();
			}
		},
		[onClick],
	);

	const chevronElement = isVisualRefreshEnabled() ? (
		<Box padding="space.075" paddingBlockStart="space.025">
			<ChevronIcon
				testId={`sidebar.expand-button.container-${id}`}
				color={token('color.icon')}
				label={label}
				size="small"
			/>
		</Box>
	) : (
		<ChevronOld
			testId={`sidebar.expand-button.container-${id}`}
			primaryColor={token('color.icon')}
			label={label}
			size={size}
		/>
	);

	// Performance optimisation - only render interactive button upon proxy interactions
	if (!isActive) {
		return chevronElement;
	}

	return (
		<Container
			role="button"
			aria-expanded={isExpanded}
			onClick={onClick}
			onKeyDown={onKeydown}
			isVisible={isVisible}
			removeCaret={removeCaret}
			size={size}
			tabIndex={tabIndex}
		>
			{chevronElement}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	size?: 'small' | 'medium' | 'large' | 'xlarge';
	isVisible?: boolean;
	removeCaret?: boolean;
}>(
	{
		position: 'relative',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		top: (props) => (props.size === 'medium' ? `-${gridSize / 2}px` : '0'),
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props) => (props.isVisible ? 1 : 0),
		padding: 0,
		margin: 0,
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:focus:not(:focus-visible)': {
			boxShadow: 'none',
		},
		flex: '0 0 auto',
		outline: 'none',
		transition: 'box-shadow 0.2s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
		'&:focus-visible': {
			boxShadow: `inset 0 0 0 2px ${token('color.text.selected')}`,
			opacity: 1,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (props.removeCaret ? { display: 'none' } : { display: 'flex' }),
);
